import { isSupported } from 'firebase/messaging'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { isNativePlatform } from '@/lib/platform/platformDetection'

/**
 * Push permission as the app needs to reason about it — deliberately coarser
 * than either platform's own enum.
 *
 * `prompt` means "never asked", which is the state `registerPush()` alone cannot
 * express: its `denied` says the user refused, and says nothing about whether we
 * ever asked. A caller needs that difference, because a refusal is unrecoverable
 * in-app while "not asked yet" is the whole opportunity.
 */
export type PushPermission = 'prompt' | 'granted' | 'denied' | 'unsupported'

/**
 * Read the CURRENT permission without prompting. None of the calls here can
 * surface an OS dialog: the native plugin's `checkPermissions` is a pure query
 * and `Notification.permission` is a synchronous property read.
 *
 * NOTE (native, Android < 13): the plugin hard-returns `granted` below API 33
 * because POST_NOTIFICATIONS did not exist yet, regardless of any real user
 * choice. Our minSdk is 26, so Android 8–12 devices report `granted` here and
 * never `prompt` — which is why prompt eligibility must treat `granted` as
 * askable rather than as "already handled" (see pushPromptDecision.ts).
 */
export async function getPushPermission(): Promise<PushPermission> {
  if (isNativePlatform()) {
    try {
      const { receive } = await FirebaseMessaging.checkPermissions()
      if (receive === 'granted') return 'granted'
      if (receive === 'denied') return 'denied'
      // 'prompt' | 'prompt-with-rationale' — both mean "we may still ask".
      return 'prompt'
    } catch {
      return 'unsupported'
    }
  }

  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported'
  }
  if (!(await probeWebPushSupport())) {
    return 'unsupported'
  }

  switch (Notification.permission) {
    case 'granted':
      return 'granted'
    case 'denied':
      return 'denied'
    default:
      return 'prompt'
  }
}

/**
 * Cached result of Firebase's web capability probe.
 *
 * `isSupported()` is async and opens an IndexedDB connection to check
 * availability, so awaiting it immediately before `Notification.requestPermission()`
 * puts an async gap between the user's click and the permission request. WebKit
 * ties notification requests to a live user gesture, so that gap risks the OS
 * dialog never appearing (or resolving as dismissed). Priming the probe well
 * ahead of the click lets `registerPush()` request permission as its first act
 * after the tap.
 *
 * This ordering already shipped behind the settings "Enable" button before the
 * soft prompt existed — the cache is what makes the request-first ordering
 * possible, not a new requirement introduced by it.
 */
let webSupportProbe: Promise<boolean> | undefined
let webSupportResult: boolean | undefined

/** Start (or reuse) the capability probe. Safe to call repeatedly. */
export function probeWebPushSupport(): Promise<boolean> {
  webSupportProbe ??= isSupported()
    .then(supported => {
      webSupportResult = supported
      return supported
    })
    .catch(() => {
      webSupportResult = false
      return false
    })
  return webSupportProbe
}

/**
 * Synchronous read of the primed probe. `undefined` means "not resolved yet",
 * which callers must treat as "unknown" rather than as "unsupported".
 */
export function getProbedWebPushSupport(): boolean | undefined {
  return webSupportResult
}

/** Test seam — drops the memoised probe so each case starts clean. */
export function resetWebPushSupportProbeForTests(): void {
  webSupportProbe = undefined
  webSupportResult = undefined
}
